import React, { memo, useState } from 'react';
import './offer-card-banner-mobile.css';
import { usePathname } from 'next/navigation';
import { useGetCurrentUser } from '@wwe-portal/web/providers/queries/user-info';
import { useTranslations } from 'next-intl';
import { useResponsive } from '@wwe-portal/ui/hooks/use-responsive';
import { useIsAuthenticated } from '@wwe-portal/web/providers/stores/auth-store';
import { useGetCurrencies } from '@wwe-portal/web/providers/queries/offer';
import { Button } from '@wwe-portal/ui/components/button/button';
import { IcoMoonIcon } from '@wwe-portal/ui/components/icons';
import { cn } from '@wwe-portal/ui/lib/lib';
import { TimeCountDown } from '../../../../../packages/ui/features/offer/component/time-countdown';
import Progress from '@wwe-portal/web/app/(default-background)/calendar/EventsSlider/EventsSliderItem/Progress';
import TooltipWrapper from '@wwe-portal/ui/features/club-store/milestone/tooltip';
import LoadAnimateButton from '../load-animate-button';
import { tryFormatThousands } from '@wwe-portal/utils/format';
import { DynamicImage } from '@wwe-portal/ui/components/dynamic-image';
import { getDailyOfferAvailableDays } from '@wwe-portal/web/utils/offer/helper';
import { OfferItem } from '@wwe-portal/entities/offer.type';

interface OfferItemWithLabel extends OfferItem {
  label?: string;
}

function OfferCardBannerMobile({
  price,
  offer,
  offerType,
  onHandlePurchase,
  onHandleClickViewMore = (offer, index, totalBanners, setShow) => {},
  transactionStarted,
  closeCallback,
  isInFuture,
  hasProgress = false,
  isInModal = false,
  titleText,
  hasWebCoinPrice = false,
  webCoinPrice = '',
  ruby = false,
  webcoin = false,
  index,
  totalBanners = 0,

  buttonText = null,
  showShowBannerText = true,
}) {
  /**
   * show hover tate flag
   */
  const [show, setShow] = useState(false);
  const pathname = usePathname();
  const currentUser = useGetCurrentUser();
  /**
   * Use translation hook
   * @see https://react.i18next.com/
   */
  const t = useTranslations();
  /**
   * Use window size custom hook
   */
  const { isMobile } = useResponsive();

  /**
   * Use Select hook
   * Using react-redux
   * Get Global -> isAuthenticated
   * Get Global -> currentUser
   * Get active currencies
   */
  const isAuthenticated = useIsAuthenticated();
  const { data: currenciesResponse } = useGetCurrencies();
  const activeCurrencies = currenciesResponse?.data.data ?? [];
  const isFreeClaimTournamentOffer =
    offerType === 'tournament' &&
    !offer?.AlternativeCostAmount &&
    offer?.OfferType === 'AlternativeCurrency';

  /**
   * Render Cash Pack Card Name
   * @param contents {array}
   */
  const renderCashPackCardHeader = (contents) => {
    if (contents && Array.isArray(contents) && contents.length) {
      return tryFormatThousands(+contents[0].split('x').join(''));
    }

    return '';
  };

  /**
   * Check if All necessary data exists
   * for calculating Bonus % and Strike-through price
   */
  const isDataExistsForStrikeAndBonus =
    offer.Price &&
    offer.defaultCurrency &&
    offer.Price?.[offer.defaultCurrency] &&
    offer.DiscountPercentage &&
    offer.DiscountPercentage > 0;

  const strikeThroughPrice = isDataExistsForStrikeAndBonus
    ? offer.Price?.[offer.defaultCurrency] /
      (1 - offer.DiscountPercentage / 100)
    : 0;

  const bonusPercentage = isDataExistsForStrikeAndBonus
    ? ((strikeThroughPrice - offer.Price?.[offer.defaultCurrency]) /
        offer.Price?.[offer.defaultCurrency]) *
      100
    : 0;

  const rubiesExistInOfferContent =
    offer.OfferItemContent &&
    offer.OfferItemContent.length &&
    offer.OfferItemContent.filter(
      (content) =>
        content.includes('rubies') ||
        content.includes('Rubies') ||
        content.includes('ruby') ||
        content.includes('Ruby')
    ).length > 0;

  const renderOfferItems = (
    { url, images, quantity, itemID, label }: OfferItemWithLabel,
    index: number
  ) => (
    <div key={index} className="relative flex flex-col">
      <DynamicImage
        alt={itemID}
        className={'img-fluid mr-1 h-[75px] w-[70px]'}
        defaultUrl={images?.url}
        dynamicUrl={url}
        resizedWidth={64}
      />

      {!strikeThroughPrice ? (
        <p className="item-quantity mb-0 text-center font-bold">
          {quantity ? `x${quantity}` : 'x1'}
        </p>
      ) : null}
      {label && (
        <div className="absolute right-1 top-[50%] w-full -translate-y-1/2 -rotate-12 text-nowrap stroke-1 text-xs font-semibold text-[#E3E7F4]">
          {label}
        </div>
      )}
    </div>
  );

  const renderButton = () => {
    const isWithTooltip =
      !offer.PortalAvailability && offerType === 'daily' && isAuthenticated;

    let availability = '';
    if (isWithTooltip) {
      availability = getDailyOfferAvailableDays(offer.schedules);
    }
    return isWithTooltip ? (
      <TooltipWrapper
        content={
          <div style={{ maxWidth: '200px' }}>
            Arriving for purchase on {availability}{' '}
          </div>
        }
        className="w-full"
      >
        <Button
          disabled
          onClick={() => {
            if (transactionStarted === offer.OffersID) return null;
            if (isInModal) {
              closeCallback();
            }
            return onHandlePurchase(
              offer,
              'special_offers',
              index,
              totalBanners
            );
          }}
          data-id={`${hasProgress ? 'home_' : ''}shop_offers_button_${offer?.OfferSectionID}`}
          variant="orange"
          className={cn(
            `mx-0 mb-[10px] flex h-10 w-full items-center justify-center px-[30px] py-0`,
            {
              'mb-[15px] px-[10px]': hasProgress,
            }
          )}
        >
          {transactionStarted === offer.OffersID ? (
            <div className="p-[10px]">
              <LoadAnimateButton bodySize="text-4" />
            </div>
          ) : (
            <p className="m-0 p-[10px]">{t('coming_soon')}</p>
          )}
        </Button>
      </TooltipWrapper>
    ) : (
      <Button
        disabled={
          (!offer.StockQuantity || isInFuture) && isAuthenticated && currentUser
        }
        onClick={() => {
          if (transactionStarted === offer.OffersID) return null;
          if (isInModal) {
            closeCallback();
          }
          return onHandlePurchase(offer, 'special_offers', index, totalBanners);
        }}
        data-id={`${hasProgress ? 'home_' : ''}shop_offers_button_${offer?.OfferSectionID}`}
        variant="orange"
        className={cn(
          `mx-0 flex h-10 w-full items-center justify-center px-[30px] py-0`,
          {
            'mb-[15px] px-[10px]': hasProgress,
          }
        )}
        style={{
          marginBottom: 0,
        }}
      >
        {transactionStarted === offer.OffersID ? (
          <div className="p-[10px]">
            <LoadAnimateButton bodySize="text-4" />
          </div>
        ) : buttonText ? (
          <p className="m-0 p-[10px]">{buttonText}</p>
        ) : isInFuture && isAuthenticated && currentUser ? (
          <p className="m-0 p-[10px]">{t('not_ready')}</p>
        ) : !offer.StockQuantity && isAuthenticated && currentUser ? (
          offerType === 'rewards' || isFreeClaimTournamentOffer ? (
            <p className="m-0 p-[10px]">{t('claimed')}</p>
          ) : (
            <p className="m-0 p-[10px]">{t('purchased')}</p>
          )
        ) : (
          <p
            className={cn(
              `mb-0 ml-[5px] flex items-center p-[10px] text-base font-semibold`,
              {
                'py-[10px]': isInModal,
              }
            )}
          >
            {offerType === 'rewards' || isFreeClaimTournamentOffer ? (
              <span className="mb-0 text-base font-semibold text-white">
                {t('claim')}
              </span>
            ) : (
              <>
                {hasWebCoinPrice ? (
                  <IcoMoonIcon iconName="web-coin-circle" className="size-4" />
                ) : (
                  <img src="/assets/icons/money-dollar.svg" />
                )}
                <span
                  className={`mb-0 ml-[1px] text-nowrap px-[5px] font-semibold`}
                >
                  {`${tryFormatThousands(hasWebCoinPrice ? webCoinPrice : price)} ${
                    hasWebCoinPrice && offerType === 'daily'
                      ? ''
                      : offer.defaultCurrency
                  }`}
                </span>
              </>
            )}
          </p>
        )}
      </Button>
    );
  };

  return (
    <div
      className={cn('shop-banner relative rounded-xl', {
        'widget-shop-banner overflow-hidden': hasProgress,
        'mb-[30px] pb-[40px]': !hasProgress,
      })}
    >
      <div
        className={`shop-banner__container relative flex overflow-hidden rounded-xl ${
          show ? 'info-container-blurred' : ''
        }`}
      >
        <div
          className={cn('shop-banner__content relative', {
            'w-full': hasProgress,
            'min-w-[300px] p-[15px] pb-0': isInModal,
          })}
        >
          {isInModal ? (
            <div className="relative mb-[15px] mt-[1px] flex w-full rounded-xl">
              {offerType === 'web-coin' ? (
                <div
                  className={`shop-daily-card-item-energy ${
                    offer.hasGlow ? 'shop-daily-card-item-energy-extra' : ''
                  } flex justify-center`}
                  style={{
                    width: 100,
                    height: 100,
                    margin: '0 auto',
                    maxWidth: 100,
                    maxHeight: 100,
                  }}
                >
                  <img
                    src={offer.OfferItemImage[0]}
                    className="img-fluid shop-daily-card-item-energy-img"
                    alt="feature_card_image"
                  />
                </div>
              ) : (
                <div
                  className="flex w-full items-center justify-center rounded-xl"
                  style={{
                    backgroundImage: `url(${offer.OfferBackgroundImage})`,
                    backgroundSize: 'contain',
                    backgroundRepeat: 'no-repeat',
                    backgroundPosition: 'center',
                    height: 150,
                  }}
                >
                  {offer?.offerItems?.length &&
                    offer.offerItems.map(renderOfferItems)}
                  {offer?.factionGiftItems?.length &&
                    offer.factionGiftItems.map(renderOfferItems)}
                </div>
              )}
              {bonusPercentage ? (
                !offer.StockQuantity &&
                isAuthenticated &&
                currentUser ? null : (
                  <div className="badge">
                    <img src="/assets/icons/ShopBadgeDesktop.svg" />
                    <p className="shop-banner-badge-text">
                      {Math.floor(bonusPercentage)}%
                    </p>
                  </div>
                )
              ) : null}
            </div>
          ) : null}
          {showShowBannerText && (
            <p
              className={cn(
                'shop-banner__content__special text-yellow800 mb-0 w-full text-sm font-medium uppercase',
                {
                  'text-xs': isInModal,
                }
              )}
            >
              {titleText
                ? titleText
                : hasProgress
                  ? t('flash_deal')
                  : t('shop_offers')}
            </p>
          )}
          <h3 className="shop-banner__content__storeItem font-semibold">
            {offerType === 'cash-packs'
              ? `${renderCashPackCardHeader(offer.OfferItemTexts)} x ${t('cash_pack')}`
              : offer.StoreItemName}
          </h3>
          <p
            className={`shop-banner__content__description text-text400 mb-[10px] text-sm`}
          >
            {offer.StoreItemDescription || t('special_offer_description')}
          </p>
          <div
            data-id="left-count"
            className={`left-count flex items-center`}
            id={index === 0 ? 'banner-left-count' : ''}
            style={{ marginBottom: 15 }}
          >
            {offer.StockQuantity >= 1000 ? (
              <span className="icon-infinity-fill mr-[5px] text-base" />
            ) : (
              <span className="mr-[1px]">{offer?.StockQuantity}</span>
            )}
            Left
          </div>
          <div className="shop-banner_detail flex w-full items-end">
            <div
              className={`shop-banner__description text-back200 mb-0 flex w-full items-center rounded-xl px-[10px] pt-[10px]`}
              style={{
                maxWidth: hasProgress ? '100%' : '400px',
                marginBottom: 0,
              }}
            >
              <ul
                className={`scrollbar-moz text-text400 mb-0 w-full pl-[15px] pt-[10px]`}
              >
                {offer?.OfferItemContent?.map((content, index) => {
                  return (
                    <li
                      key={`${content.trim()}-${index}`}
                      className="relative mb-[15px]"
                    >
                      {content}
                    </li>
                  );
                })}
                {activeCurrencies.length && offer.InitialPrice != null
                  ? (isInModal && offerType === 'rewards') ||
                    (isInModal &&
                      offerType === 'daily' &&
                      pathname !== '/special-offers') ||
                    (isInModal && isFreeClaimTournamentOffer)
                    ? null
                    : activeCurrencies.map((currency) =>
                        currency.name === 'Web Coin' ||
                        currency.name === 'Ruby' ? (
                          (isInModal &&
                            offerType === 'web-coin' &&
                            currency.name === 'Web Coin') ||
                          (currency.name === 'Ruby' &&
                            rubiesExistInOfferContent) ? null : (
                            <li
                              className="relative mb-[15px]"
                              key={currency.id}
                              id={
                                index === 0
                                  ? currency.name === 'Web Coin'
                                    ? 'banner-details-web-coin'
                                    : currency.name === 'Ruby'
                                      ? 'banner-details-ruby'
                                      : ''
                                  : ''
                              }
                            >
                              {currency.rate *
                                Math.round(offer.InitialPrice.USD)}{' '}
                              {currency.name}
                            </li>
                          )
                        ) : (
                          <li className="relative mb-[15px]" key={currency.id}>
                            {Math.round(currency.rate * offer.InitialPrice.USD)}{' '}
                            {currency.name}
                          </li>
                        )
                      )
                  : ''}
                {ruby || webcoin ? null : (
                  <li className="relative mb-[15px]">VIP {t('points')}</li>
                )}
              </ul>
            </div>
            {offerType === 'cash-packs' ||
            offerType === 'web-coin' ||
            offerType === 'daily' ? null : hasProgress && isInModal ? (
              <div className="shop-banner-progress countdown-timer mb-5 mt-[1px]">
                <TimeCountDown endDate={offer.endDate} />
              </div>
            ) : null}
            <div
              className={`shop-banner__button flex w-full flex-col`}
              style={{
                minHeight:
                  hasProgress && strikeThroughPrice
                    ? isMobile
                      ? '90px'
                      : '140px'
                    : 'inherit',
              }}
            >
              {!offer.StockQuantity &&
              isAuthenticated &&
              currentUser ? null : strikeThroughPrice ? (
                <div className={'justify-content-between flex'}>
                  <Button
                    variant="blue"
                    className={cn(
                      `mx-0 mb-[10px] flex h-10 w-full items-center justify-center px-[30px] py-0`,
                      {
                        'mb-[15px] px-[10px]': hasProgress,
                      }
                    )}
                  >
                    <img src="/assets/icons/money-dollar.svg" />
                    <p
                      className={`mb-0 ml-[5px] py-[10px] text-base font-semibold underline`}
                    >
                      <s>{`${tryFormatThousands(strikeThroughPrice.toFixed(2))} ${
                        hasWebCoinPrice && offerType === 'daily'
                          ? ''
                          : offer.defaultCurrency
                      }`}</s>
                    </p>
                  </Button>
                  {isInModal ? null : hasProgress ? (
                    <div
                      className="shop-banner-information ml-[15px] text-xs"
                      onClick={() =>
                        onHandleClickViewMore(
                          offer,
                          index,
                          totalBanners,
                          setShow
                        )
                      }
                    >
                      {/* <span className={cn('icon-information-fill h4 h-full')} /> */}
                      <IcoMoonIcon iconName="information-fill" />
                    </div>
                  ) : null}
                </div>
              ) : null}
              <div
                className={`flex justify-between ${
                  isMobile &&
                  (offerType === 'cash-packs' ||
                    offerType === 'web-coin' ||
                    offerType === 'daily')
                    ? 'mt-5'
                    : ''
                }`}
              >
                {renderButton()}
                {hasProgress &&
                isMobile &&
                !strikeThroughPrice &&
                !isInModal ? (
                  <div
                    className="shop-banner-information ml-[15px] text-xs"
                    onClick={() =>
                      onHandleClickViewMore(offer, index, totalBanners, setShow)
                    }
                  >
                    {/* <span className={cn('icon-information-fill h4 h-100')} /> */}
                    <IcoMoonIcon iconName="information-fill" />
                  </div>
                ) : null}
              </div>
            </div>
          </div>
          {hasProgress && !isInModal ? (
            <div className="shop-banner-progress mt-[30px]">
              <Progress endDate={offer.EndDate} startDate={offer.StartDate} />
            </div>
          ) : null}
        </div>
      </div>
      {!hasProgress ? (
        <div className="shop-banner-progress shop-banner-progress-bar mt-[30px]">
          <TimeCountDown endDate={offer.endDate} />
        </div>
      ) : null}
    </div>
  );
}

export default memo(OfferCardBannerMobile);
